import { encounterCreatureField } from '../../shared/array-types';
import { ecId, type Row } from '../ability/context';
import { deathInputMemberIds } from '../death/input-members';
import { collectTriggerDefinitionInputs } from '../reactions/definition-inputs';
import { zeroVitalityWitnessFact, type TriggerReaders } from '../reactions/trigger-dispatch';
import { statusRows } from '../statuses/expiry';
import { savedAttackMembers } from './attack-members';
import { containsTriggerKey } from './weapon-attack-extra-damage';

/** The dispatcher reads ordinary spell definitions to prove no trigger; it never reads
 * their cast payloads. An explicitly dispatched spell still owns its full closure. */
export function attackTriggerDefinitionNeeded(definition: Row, read: Pick<TriggerReaders, 'itemField' | 'value'>): boolean {
    return read.itemField(definition, 'item_type') !== 'spell' || containsTriggerKey(definition)
        || read.value(definition, 'spell_casting_time') === 'reaction';
}

/** An attack can affect its participants, their physical status connections and controllers,
 * and zero-Vitality witnesses. Unrelated spellbooks are not attack inputs. Unknown witness
 * facts remain conservative; the executor still owns range, sight and reaction admission. */
export function attackInputMembers(encounter: Row, actor: Row, target: Row, read: TriggerReaders,
    additionalTargets: readonly Row[] = [], dispatched: readonly Row[] = []): Row[] {
    const members = savedAttackMembers(encounter);
    const roots = new Set([actor, target, ...additionalTargets].map(ecId).filter(Boolean));
    for (const id of roots) {
        const controller = String(encounterCreatureField(members.get(id)?.encounter_creatures, 'controller_encounter_creature_id') ?? '').toUpperCase();
        if (members.has(controller)) roots.add(controller);
    }
    for (const [id, member] of members) {
        if (!roots.has(id) && zeroVitalityWitnessFact(read, member) !== 'none') roots.add(id);
    }
    const reached = new Set(deathInputMemberIds(encounter, [...roots]));
    const scoped = [...members].filter(([id]) => reached.has(id)).map(([, member]) => member);
    // An authored area reaction may reach further holders. Preserve that lane's full input
    // inventory until its own area expansion provides an exact participant set.
    const discovered = collectTriggerDefinitionInputs(scoped, read, member => statusRows(member, 'target'));
    const area = (def: Row): boolean => read.value(def, 'aoe_type') != null || read.entries(def, 'aoe_type').length > 0;
    if (dispatched.some(area) || discovered.definitions.some(def => area(def) && containsTriggerKey(def))) return [...members.values()];
    return scoped;
}
